import { useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, FileText } from "lucide-react";
import { EASE, lineMask } from "@/lib/motion";
import { scrollToId } from "@/lib/lenis";
import { NetworkBadge } from "@/components/NetworkBadge";
import { PROJECT_NAME, PROJECT_SUBTITLE } from "@/config/actra";

const SIGNAL_STAGES = ["TASK", "AGENT", "ACTION", "EVIDENCE", "PROOF"];

const DataLines = () => (
    <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-50" aria-hidden="true" preserveAspectRatio="none">
        <path
            d="M-40,180 C280,120 520,260 860,200 S1400,120 1720,220"
            fill="none"
            stroke="#1677FF"
            strokeOpacity="0.16"
            strokeWidth="1"
            strokeDasharray="5 16"
            className="animate-dash"
        />
        <path
            d="M-40,420 C300,360 640,520 980,430 S1440,360 1760,470"
            fill="none"
            stroke="#31D8FF"
            strokeOpacity="0.10"
            strokeWidth="1"
            strokeDasharray="4 20"
            className="animate-dash"
            style={{ animationDuration: "42s" }}
        />
        <path
            d="M-40,640 C360,600 720,700 1080,640 S1500,560 1780,660"
            fill="none"
            stroke="#1677FF"
            strokeOpacity="0.08"
            strokeWidth="1"
            strokeDasharray="3 24"
            className="animate-dash"
            style={{ animationDuration: "55s" }}
        />
    </svg>
);

const SignalPanel = () => (
    <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.1, duration: 1, ease: EASE }}
        className="pointer-events-none absolute bottom-24 right-10 hidden w-60 xl:block"
        aria-hidden="true"
    >
        <div className="border border-white/[0.08] bg-[#040811]/70 p-5 backdrop-blur-md">
            <p className="label-tech mb-4">VERIFICATION SIGNAL</p>
            <div className="relative flex flex-col gap-3">
                <motion.span
                    className="absolute left-[3px] top-1 h-1.5 w-1.5 rounded-full bg-[#31D8FF] shadow-[0_0_12px_#31D8FF]"
                    animate={{ top: ["2%", "96%"] }}
                    transition={{ duration: 3.6, repeat: Infinity, ease: "linear" }}
                />
                {SIGNAL_STAGES.map((s, i) => (
                    <motion.div
                        key={s}
                        className="relative flex items-center gap-3 pl-6"
                        animate={{ opacity: [0.35, 1, 0.35] }}
                        transition={{ duration: 3.6, repeat: Infinity, delay: i * 0.72, times: [0, 0.5, 1] }}
                    >
                        <span className="absolute left-0 top-2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-[#1677FF]" />
                        {i < SIGNAL_STAGES.length - 1 && (
                            <span className="absolute left-[-1px] top-3.5 h-[calc(100%+2px)] w-px bg-gradient-to-b from-[#1677FF]/60 to-transparent" />
                        )}
                        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-ink">{s}</span>
                    </motion.div>
                ))}
            </div>
        </div>
    </motion.div>
);

export const Hero = () => {
    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 900], [0, 160]);
    const fade = useTransform(scrollY, [0, 500], [1, 0]);

    useEffect(() => {
        const go = (e) => scrollToId(e.detail);
        window.addEventListener("actra:goto", go);
        return () => window.removeEventListener("actra:goto", go);
    }, []);

    return (
        <section data-testid="hero" className="relative flex min-h-[100svh] items-center overflow-hidden">
            {/* Environment */}
            <motion.div className="absolute inset-0" style={{ y: bgY }}>
                <motion.img
                    src="/actra-background.png"
                    alt="ACTRA — Arc mainnet environment"
                    className="h-[115%] w-full object-cover object-[68%_center] lg:object-center"
                    initial={{ scale: 1.14 }}
                    animate={{ scale: 1.06 }}
                    transition={{ duration: 2.4, ease: EASE }}
                    fetchpriority="high"
                />
            </motion.div>
            {/* Targeted readability overlays only */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#040811] via-[#040811]/72 to-transparent" />
            <div className="absolute inset-0 bg-[#040811]/35 lg:hidden" />
            <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-[#040811] to-transparent" />
            <div className="absolute inset-0 bg-noise opacity-[0.05] mix-blend-overlay" />
            <DataLines />

            <SignalPanel />

            <motion.div className="shell relative z-10 pb-28 pt-32 md:pt-36" style={{ opacity: fade }}>
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05, duration: 0.8, ease: EASE }}
                    className="label-tech mb-6 flex items-center gap-3"
                >
                    <span className="inline-block h-[7px] w-[7px] bg-[#1677FF]" aria-hidden="true" />
                    {PROJECT_SUBTITLE}
                </motion.p>

                <h1 className="font-display text-[clamp(3.2rem,10vw,8rem)] font-bold uppercase leading-[0.94] tracking-tightest">
                    <span className="block overflow-hidden pb-1">
                        <motion.span className="block" {...lineMask(0.15)}>
                            VERIFY
                        </motion.span>
                    </span>
                    <span className="block overflow-hidden pb-2">
                        <motion.span className="block" {...lineMask(0.3)}>
                            EVERY <span className="text-[#1677FF]">ACTION.</span>
                        </motion.span>
                    </span>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.65, duration: 0.9, ease: EASE }}
                    className="mt-7 max-w-xl text-base leading-relaxed text-[#B7C2D4] md:text-lg"
                >
                    {PROJECT_NAME} is the verification layer for the Action Economy — transforming autonomous actions into
                    verifiable proof, reputation, and settlement on Arc.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.9, ease: EASE }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <button
                        data-testid="hero-explore-protocol"
                        onClick={() => scrollToId("#action-economy")}
                        className="inline-flex items-center gap-3 bg-[#1677FF] px-8 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition-all duration-300 hover:bg-[#3D8BFF] hover:shadow-[0_0_36px_rgba(22,119,255,0.4)]"
                    >
                        EXPLORE PROTOCOL <ArrowRight size={14} />
                    </button>
                    <Link
                        to="/docs"
                        data-testid="hero-view-docs"
                        className="inline-flex items-center gap-3 border border-white/20 bg-[#040811]/40 px-8 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-ink backdrop-blur-sm transition-all duration-300 hover:border-[#31D8FF] hover:text-[#31D8FF]"
                    >
                        <FileText size={14} /> VIEW DOCS
                    </Link>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.9, ease: EASE }}
                    className="mt-12 flex flex-wrap items-center gap-5"
                >
                    <NetworkBadge />
                    <span className="label-tech hidden md:block">BUILT FOR ARC</span>
                    <Link
                        to="/tasks"
                        data-testid="hero-launch-app"
                        className="group inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-[#31D8FF] transition-colors hover:text-white"
                    >
                        LAUNCH APP
                        <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </motion.div>
            </motion.div>

            <motion.div
                className="absolute bottom-7 left-1/2 z-10 -translate-x-1/2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                aria-hidden="true"
            >
                <div className="h-10 w-px bg-gradient-to-b from-[#1677FF] to-transparent" />
            </motion.div>
        </section>
    );
};

export default Hero;
